import React, { useEffect, useRef, useState, useCallback } from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useSelector, shallowEqual } from "react-redux";
import classnames from "classnames";
import {
  makeSpotlightPeersSelector,
  videoBoxesSelector,
} from "../../helpers/Selectors";
import SpeakerPeer from "../Presence/SpeakerPeer";
import debounce from "../../utils/debounce";
import { intl } from "../../intl";

const RATIO = 1.334;

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      borderRadius: 24,
      height: "100%",
      width: "100%",
      // display: "grid",
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr 0.25fr",
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        width: "100%",
      },
    },
    speaker: {
      borderRadius: 24,
      gridArea: "1 / 1 / 1 / 1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
        flexDirection: "column",
      },
    },
    filmStrip: {
      gridArea: "2 / 1 / 2 / 1",
    },
    filmItem: {
      display: "flex",
      borderRadius: 24,
      [theme.breakpoints.down("xs")]: {
        borderRadius: 0,
      },
      "&.selected": {
        borderColor: "rgba(55, 126, 255, 1)",
      },
      "&.active": {
        opacity: "0.6",
      },
    },
    hiddenToolBar: {
      paddingTop: 0,
      transition: "padding .5s",
    },
    showingToolBar: {
      transition: "padding .5s",
    },
    participantsLeft: {
      color: "white",
      background: "black",
      height: 200,
      width: 265,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 24,
    },
  })
);

const MiniView = () => {
  const classes = useStyles();
  const rootRef = useRef();
  const activePeerRef = useRef();

  const [lastSpeaker, setLastSpeaker] = useState();
  const [speakerWidth, setSpeakerWidth] = useState();
  const [speakerHeight, setSpeakerHeight] = useState();

  const activeSpeakerId = useSelector((state) => state.room.activeSpeakerId);
  const selectedPeerId = useSelector((state) => state.room.selectedPeerId);
  const peers = useSelector((state) => state.peers);
  const room = useSelector((state) => state.room);
  const consumers = useSelector((state) => state.consumers);
  const myId = useSelector((state) => state.me.id);
  const spotlights = useSelector(
    (state) => makeSpotlightPeersSelector(state)(state),
    shallowEqual
  );
  const isMini = useSelector((state) => state.call.displayMini);
  const session = useSelector((state) => state.remoteControl);
  const isController =
    session &&
    session.status === "active" &&
    session.controllerPeerId === myId;
  const toolbarsVisible = useSelector((state) => state.room.toolbarsVisible);
  const permanentTopBar = useSelector(
    (state) => state.settings.permanentTopBar
  );

  // Find the name of the peer which is currently speaking. This is either
  // the latest active speaker, or the manually selected peer, or, if no
  // person has spoken yet, the first peer in the list of peers.
  const getActivePeerId = useCallback(() => {
    if (selectedPeerId && peers[selectedPeerId]) {
      return selectedPeerId;
    }

    if (lastSpeaker && peers[lastSpeaker]) {
      return lastSpeaker;
    }

    const peerIds = Object.keys(peers);

    if (peerIds.length > 0) {
      return peerIds[0];
    }
  }, [selectedPeerId, peers, lastSpeaker]);
  const activePeerId = getActivePeerId();
  const displayPeerId =
    isController &&
    session.hostPeerId &&
    peers[session.hostPeerId]
      ? session.hostPeerId
      : activePeerId;

  const boxes = useSelector((state) => videoBoxesSelector(state));

  const updateDimensions = useCallback(() => {
    const root = rootRef.current;
    const availableWidth = root ? root.clientWidth : 0;
    const speaker = activePeerRef.current;
    if (speaker) {
      let speakerWidth = availableWidth;
      let speakerHeight = speakerWidth / RATIO;
      setSpeakerWidth(speakerWidth);
      setSpeakerHeight(speakerHeight);
    }
  }, [
    boxes,
    permanentTopBar,
    toolbarsVisible,
    consumers,
    peers,
    getActivePeerId,
  ]);

  useEffect(() => {
    const debouncedUpdate = debounce(updateDimensions, 250);
    // window.resize event listener
    window.addEventListener("resize", debouncedUpdate);

    updateDimensions();

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
    };
  }, [updateDimensions]);

  useEffect(() => {
    if (activeSpeakerId != null && activeSpeakerId !== myId) {
      setLastSpeaker(activeSpeakerId);
    }
  }, [activeSpeakerId, myId]);

  return (
    <div
      className={classnames(
        classes.root,
        toolbarsVisible || permanentTopBar
          ? classes.showingToolBar
          : classes.hiddenToolBar
      )}
      ref={rootRef}
    >
      <div className={classes.speaker} ref={activePeerRef}>
        {peers[displayPeerId] ? (
          (!room.webinar || (room.webinar && spotlights.length !== 0)) && (
            <SpeakerPeer
              id={displayPeerId}
              style={{
                width: speakerWidth,
                height: speakerHeight,
              }}
              onFilmStrip={true}
              isMini={isMini}
            />
          )
        ) : (
          <div className={classes.participantsLeft}>
            {intl.formatMessage({
              id: "messaging.groupCall.allParticipantLeft",
              defaultMessage: "All participants left.",
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniView;
